import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { apiRequest } from '../lib/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiAlertTriangle, FiLoader, FiShield } = FiIcons;

const PayUSettingsModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    merchantKey: '',
    merchantSalt: '',
    mode: 'test' // 'test' or 'prod'
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      const d = await getDoc(doc(db, 'paymentConfig', 'payu'));
      if (d.exists()) setFormData(d.data());
    };
    load();
  }, []);

  const handleTest = async () => {
    if (!formData.merchantKey || !formData.merchantSalt) {
      alert("Please enter both Merchant Key and Salt");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      // Save temporarily to test
      await setDoc(doc(db, 'paymentConfig', 'payu'), { ...formData, type: 'payu' }, { merge: true });
      const result = await apiRequest('/test-payu-connection');
      setTestResult({ success: true, message: result.message });
    } catch (e) {
      setTestResult({ success: false, message: e.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setDoc(doc(db, 'paymentConfig', 'payu'), {
        ...formData,
        type: 'payu',
        updatedAt: new Date().toISOString()
      });
      onClose();
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
              <SafeIcon icon={FiShield} className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">PayU Gateway</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Merchant Configuration</p>
            </div>
          </div>
          <button onClick={onClose} className="p-4 hover:bg-gray-50 rounded-2xl text-gray-400"><SafeIcon icon={FiX} /></button>
        </div>

        <form onSubmit={handleSave} className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 mb-2 block">Environment</label>
              <select 
                value={formData.mode} 
                onChange={(e) => setFormData({...formData, mode: e.target.value})}
                className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-orange-500 outline-none font-bold"
              >
                <option value="test">Sandbox (Testing)</option>
                <option value="prod">Production (Live)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 mb-2 block">Merchant Key</label>
              <input type="text" value={formData.merchantKey} onChange={(e) => setFormData({...formData, merchantKey: e.target.value})} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-orange-500 outline-none font-bold" placeholder="Enter Key" required />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 mb-2 block">Merchant Salt</label>
              <input type="password" value={formData.merchantSalt} onChange={(e) => setFormData({...formData, merchantSalt: e.target.value})} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-orange-500 outline-none font-bold" placeholder="••••••••" required />
            </div>
          </div>

          {testResult && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`p-5 rounded-2xl text-sm font-bold flex items-center gap-3 ${testResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
              <SafeIcon icon={testResult.success ? FiCheck : FiAlertTriangle} className="text-lg" />
              {testResult.message}
            </motion.div>
          )}

          <div className="flex justify-end gap-3 pt-6 border-t font-black">
            <button type="button" onClick={handleTest} disabled={testing} className="px-8 text-orange-600 text-xs uppercase tracking-widest hover:bg-orange-50 rounded-xl py-4 transition-all">
              {testing ? 'Verifying...' : 'Test Connection'}
            </button>
            <button type="submit" disabled={loading} className="px-10 py-4 bg-orange-600 text-white rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-orange-100 hover:bg-orange-700 transition-all">
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default PayUSettingsModal;